import { get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * REST client for the Contact Flow Tracking endpoints exposed by the cdyxcache
 * lambda under the `cdyxoutreach` API (`/cache/tracking/...`).
 *
 * All calls send the Cognito id token as a Bearer credential plus the tenant
 * `x-api-key`, matching the rest of the app's cache calls (see cacheService.js).
 * The tenant itself is derived server-side from the token's `custom:tenant`
 * claim, so it is never passed from the client.
 *
 * @category Tools
 */

/** Ordered pipeline stage buckets, matching the backend `BUCKETS`. */
export const TRACKING_STAGES = ['ingested', 'indexed', 'queued', 'completed'];

/** Human-friendly labels for each stage bucket. */
export const TRACKING_STAGE_LABELS = {
	ingested: 'Ingested',
	indexed: 'Indexed',
	queued: 'Queued',
	completed: 'Completed',
};

/**
 * Build the auth headers shared by every tracking request.
 * @param {string} apiKey Tenant API key (from UserContext).
 * @returns {Promise<{ Authorization: string, 'x-api-key': string }>}
 */
async function buildAuthHeaders(apiKey) {
	const session = await fetchAuthSession();
	const idToken = session.tokens.idToken;
	return {
		Authorization: `Bearer ${idToken}`,
		'x-api-key': apiKey,
	};
}

/**
 * Fetch per-stage counts for every active import for the caller's tenant.
 *
 * @param {string} apiKey Tenant API key.
 * @returns {Promise<{ imports: Array<{ importId: string, counts: Record<string, number>, total: number }>, totals: Record<string, number>, total: number }>}
 */
export async function getStageCounts(apiKey) {
	const headers = await buildAuthHeaders(apiKey);
	const response = await get({
		apiName: 'cdyxoutreach',
		path: '/cache/tracking/stage-counts',
		options: { headers },
	}).response;

	const data = await response.body.json();
	return {
		imports: data?.imports ?? [],
		totals: data?.totals ?? { ingested: 0, indexed: 0, queued: 0, completed: 0 },
		total: data?.total ?? 0,
	};
}

/**
 * List a page of contacts sitting in a given stage of a given import.
 *
 * `search` is page-scoped on the server (it only filters the returned page,
 * not the whole bucket), so `total` always reflects the full bucket size.
 *
 * @param {object} params
 * @param {string} params.apiKey   Tenant API key.
 * @param {string} params.importId Import name.
 * @param {string} params.stage    One of TRACKING_STAGES.
 * @param {number} [params.limit]  Page size (default 100, backend clamps to 500).
 * @param {number} [params.offset] Page offset (default 0).
 * @param {string} [params.search] Optional page-scoped substring filter.
 * @returns {Promise<{ importId: string, stage: string, total: number, offset: number, limit: number, search: string|null, searchScope: string, contacts: Array<{ id: string, externalId: string|null, name: string|null, phone: string|null, enteredAt: number }> }>}
 */
export async function getStageContacts({ apiKey, importId, stage, limit = 100, offset = 0, search }) {
	const headers = await buildAuthHeaders(apiKey);

	const queryParams = { importId, stage, limit: String(limit), offset: String(offset) };
	if (search) {
		queryParams.search = search;
	}

	const response = await get({
		apiName: 'cdyxoutreach',
		path: '/cache/tracking/contacts',
		options: { headers, queryParams },
	}).response;

	const data = await response.body.json();
	return {
		importId: data?.importId ?? importId,
		stage: data?.stage ?? stage,
		total: data?.total ?? 0,
		offset: data?.offset ?? offset,
		limit: data?.limit ?? limit,
		search: data?.search ?? null,
		searchScope: data?.searchScope ?? 'page',
		contacts: data?.contacts ?? [],
	};
}

/**
 * Fetch the full tracking record for a single contact within an import.
 *
 * @param {object} params
 * @param {string} params.apiKey    Tenant API key.
 * @param {string} params.importId  Import name.
 * @param {string} params.contactId Contact id.
 * @returns {Promise<object>} The contact tracking record: { tenant, importId,
 *   importSource, contactId, externalId, name, phone, stage, terminal,
 *   firstSeenAt, stageTimes, outcome, updatedAt }.
 */
export async function getContactRecord({ apiKey, importId, contactId }) {
	const headers = await buildAuthHeaders(apiKey);
	const response = await get({
		apiName: 'cdyxoutreach',
		path: '/cache/tracking/contact',
		options: { headers, queryParams: { importId, contactId } },
	}).response;

	return response.body.json();
}
